/**
 * uploadToR2 — uploads directly to Cloudflare R2 using S3-compatible API
 * Returns the public URL of the uploaded file
 */

const R2_ACCOUNT_ID = '036f659630ca7e5684752a40e56996ab'
const R2_ACCESS_KEY_ID = 'f872d38d2f612827b9547ab16c385234'
const R2_SECRET_ACCESS_KEY = '688734a7d753e463741ed4c40984f14323d15df32ced40c848334081ce8e6749'
const R2_BUCKET = 'bailey-family-portal'
const R2_PUBLIC_URL = 'https://pub-2a56c310fa914390bc7ecf95e89850b4.r2.dev'

async function sha256hex(message) {
  const data = typeof message === 'string' ? new TextEncoder().encode(message) : message
  const hash = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('')
}

async function hmac(key, message) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw', typeof key === 'string' ? new TextEncoder().encode(key) : key,
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']
  )
  const data = typeof message === 'string' ? new TextEncoder().encode(message) : message
  return crypto.subtle.sign('HMAC', cryptoKey, data)
}

async function hmacHex(key, message) {
  const sig = await hmac(key, message)
  return Array.from(new Uint8Array(sig)).map(b => b.toString(16).padStart(2, '0')).join('')
}

async function getSigningKey(secret, dateStamp) {
  const kDate = await hmac(`AWS4${secret}`, dateStamp)
  const kRegion = await hmac(kDate, 'auto')
  const kService = await hmac(kRegion, 's3')
  return hmac(kService, 'aws4_request')
}

export async function uploadToR2(file, path) {
  const arrayBuffer = await file.arrayBuffer()
  const host = `${R2_ACCOUNT_ID}.r2.cloudflarestorage.com`
  const endpoint = `https://${host}/${R2_BUCKET}/${path}`

  const now = new Date()
  const dateStamp = now.toISOString().slice(0, 10).replace(/-/g, '')
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '').slice(0, 15) + 'Z'

  const bodyHashHex = await sha256hex(arrayBuffer)

  const headers = {
    'host': host,
    'content-type': file.type || 'application/octet-stream',
    'x-amz-content-sha256': bodyHashHex,
    'x-amz-date': amzDate,
  }

  const sortedKeys = Object.keys(headers).sort()
  const signedHeaders = sortedKeys.join(';')
  const canonicalHeaders = sortedKeys.map(k => `${k}:${headers[k]}`).join('\n') + '\n'
  const canonicalRequest = ['PUT', `/${R2_BUCKET}/${path}`, '', canonicalHeaders, signedHeaders, bodyHashHex].join('\n')

  const credentialScope = `${dateStamp}/auto/s3/aws4_request`
  const stringToSign = ['AWS4-HMAC-SHA256', amzDate, credentialScope, await sha256hex(canonicalRequest)].join('\n')

  const signingKey = await getSigningKey(R2_SECRET_ACCESS_KEY, dateStamp)
  const signature = await hmacHex(signingKey, stringToSign)

  const authorization = `AWS4-HMAC-SHA256 Credential=${R2_ACCESS_KEY_ID}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`

  const res = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      ...headers,
      'Authorization': authorization,
    },
    body: arrayBuffer,
  })

  if (!res.ok) {
    const text = await res.text()
    throw new Error(`R2 upload failed: ${res.status} ${text}`)
  }

  return `${R2_PUBLIC_URL}/${path}`
}
